import React, { useMemo, useRef, useState } from 'react'
import { fmt, fmtFixed } from '../api.js'
import { CURRENCIES, KRW_FIELDS } from './lineFields.js'

// 기안(지출결의) 내역 표를 엑셀/CSV·붙여넣기로 채워넣는 도구.
// - 열 구성(fields)은 양식마다 다르다 (계좌이체 7열 / 외화송금 9열)
// - xlsx 라이브러리는 클릭 시점에 동적 import (초기 번들에 안 실림)
// - 머리글 행을 찾아 열을 자동 매핑하므로 열 순서가 달라도 된다

const norm = (v) => String(v ?? '').replace(/\s+/g, '').replace(/[()（）*＊]/g, '').toLowerCase()

function makeFieldOf(fields) {
  const aliases = fields
    .flatMap((f) => f.aliases.map((a) => ({ key: f.key, a })))
    .sort((x, y) => y.a.length - x.a.length)
  return (cell) => {
    const v = norm(cell)
    if (!v) return null
    const exact = aliases.find((x) => x.a === v)
    if (exact) return exact.key
    const part = aliases.find((x) => v.includes(x.a))
    return part ? part.key : null
  }
}

// 엑셀에서 큰 숫자가 지수표기(1.002E+12)로 넘어오는 경우까지 되돌린다
function cleanText(v) {
  const s = String(v ?? '').trim()
  if (/^-?\d+(\.\d+)?e[+-]?\d+$/i.test(s)) {
    const n = Number(s)
    if (Number.isFinite(n)) return n.toFixed(0)
  }
  return s
}

function parseAmount(v) {
  const s = cleanText(v).replace(/[,\s원₩\\]/g, '')
  if (!s) return ''
  const neg = /^\(.*\)$/.test(s)
  const n = Number(s.replace(/[()]/g, ''))
  if (!Number.isFinite(n)) return ''
  return String(neg ? -n : n)
}

function matchCat(v, cats) {
  const raw = String(v ?? '').trim()
  const n = norm(raw)
  if (!n) return { value: '', unmatched: '' }
  const hit = cats.find((c) => norm(`${c.code} ${c.name}`) === n || norm(c.name) === n || norm(c.code) === n)
  if (hit) return { value: `${hit.code} ${hit.name}`, unmatched: '' }
  const part = n.length >= 2 && cats.find((c) => norm(c.name) && (n.includes(norm(c.name)) || norm(c.name).includes(n)))
  if (part) return { value: `${part.code} ${part.name}`, unmatched: '' }
  return { value: '', unmatched: raw }
}

const matchCurrency = (v) => {
  const s = String(v ?? '').trim().toUpperCase()
  return CURRENCIES.includes(s) ? s : CURRENCIES[0]
}

// 머리글 행 찾기 — 앞쪽 30행 중 알아본 열이 가장 많은 행
function detectHeader(matrix, fields) {
  const fieldOf = makeFieldOf(fields)
  let best = null
  const limit = Math.min(matrix.length, 30)
  for (let r = 0; r < limit; r++) {
    const map = {}
    let hits = 0
    ;(matrix[r] || []).forEach((cell, c) => {
      const key = fieldOf(cell)
      if (key && map[key] === undefined) { map[key] = c; hits++ }
    })
    if (hits >= 2 && (map.vendor !== undefined || map.amount !== undefined)) {
      if (!best || hits > best.hits) best = { row: r, map, hits }
    }
  }
  return best
}

const TOTAL_ROW = /^(합계|소계|총계|계|총액)$/

function rowsFrom(matrix, map, startRow, cats, fields) {
  const keys = Object.keys(map)
  const hasCurrency = fields.some((f) => f.key === 'currency')
  const rows = []
  const unmatchedCats = new Set()
  let blanks = 0
  for (let r = startRow; r < matrix.length; r++) {
    const row = matrix[r] || []
    const cell = (k) => (map[k] === undefined ? '' : cleanText(row[map[k]]))
    if (keys.every((k) => !cell(k))) {
      if (++blanks >= 5) break
      continue
    }
    blanks = 0
    const vendor = cell('vendor')
    if (TOTAL_ROW.test(norm(vendor)) || TOTAL_ROW.test(norm(cell('detail')))) continue
    const cat = matchCat(cell('account_cat'), cats)
    if (cat.unmatched) unmatchedCats.add(cat.unmatched)
    rows.push({
      vendor,
      detail: cell('detail'),
      bank: cell('bank'),
      swift: cell('swift').toUpperCase(),
      account: cell('account').replace(/[\s']/g, ''),
      holder: cell('holder'),
      currency: hasCurrency ? matchCurrency(cell('currency')) : '',
      amount: parseAmount(cell('amount')),
      account_cat: cat.value,
      _srcRow: r + 1,
    })
  }
  const missing = fields.filter((f) => map[f.key] === undefined).map((f) => f.label)
  return { rows, missing, unmatchedCats: [...unmatchedCats] }
}

function extract(matrix, cats, fields) {
  const header = detectHeader(matrix, fields)
  if (!header) return { error: '머리글(거래처·금액 등)을 찾지 못했습니다. 첫 행에 열 이름이 있는지 확인하세요.' }
  return { ...rowsFrom(matrix, header.map, header.row + 1, cats, fields), header }
}

// --- 파일 읽기 -------------------------------------------------------------

function csvMatrix(text) {
  const s = text.replace(/^﻿/, '')
  const out = []
  let row = [], cell = '', inQ = false
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (inQ) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++ } else inQ = false } else cell += c
    } else if (c === '"') inQ = true
    else if (c === ',') { row.push(cell); cell = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++
      row.push(cell); out.push(row); row = []; cell = ''
    } else cell += c
  }
  row.push(cell)
  if (row.some((x) => x !== '')) out.push(row)
  return out
}

function decodeCsv(buf) {
  const utf8 = new TextDecoder('utf-8').decode(buf)
  if (!utf8.includes('�')) return utf8
  try { return new TextDecoder('euc-kr').decode(buf) } catch { return utf8 }
}

async function readFile(file) {
  const buf = await file.arrayBuffer()
  if (/\.csv$/i.test(file.name)) {
    return { sheets: ['CSV'], matrixOf: () => csvMatrix(decodeCsv(buf)) }
  }
  const XLSX = await import('xlsx')
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array' })
  return {
    sheets: wb.SheetNames,
    // raw:true — 서식이 아니라 원래 값을 읽는다 (표시서식을 거치면 긴 계좌번호가 반올림된다)
    matrixOf: (name) => XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, defval: '' }),
  }
}

async function downloadTemplate(cats, fields, fx) {
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()
  const cat = cats[0] ? cats[0].name : ''
  const sample = fx
    ? [
      ['SHANGHAI TRADING CO.', cat, '5월 원재료 대금', 'BANK OF CHINA', 'BKCHCNBJ300', '1234567890123', 'SHANGHAI TRADING CO.', 'USD', 12500.5],
      ['ABC PTY LTD', cat, '설비 잔금', 'ANZ', 'ANZBAU3M', '013-456 78901234', 'ABC PTY LTD', 'AUD', 3200],
    ]
    : [
      ['(주)에르모어', cat, '5월 광고비', '국민', '123456-01-234567', '(주)에르모어', 1000000],
      ['홍길동', '', '출장비 정산', '신한', '110-123-456789', '홍길동', 250000],
    ]
  const ws = XLSX.utils.aoa_to_sheet([fields.map((f) => f.label), ...sample])
  ws['!cols'] = fields.map((f) => ({ wch: Math.max(12, Math.round(f.width / 7)) }))
  XLSX.utils.book_append_sheet(wb, ws, fx ? '외화송금' : '지출내역')
  if (cats.length) {
    const cws = XLSX.utils.aoa_to_sheet([['계정과목코드', '계정과목명'], ...cats.map((c) => [c.code, c.name])])
    cws['!cols'] = [{ wch: 14 }, { wch: 24 }]
    XLSX.utils.book_append_sheet(wb, cws, '계정과목')
  }
  XLSX.writeFile(wb, fx ? '외화송금_내역_양식.xlsx' : '지출결의_내역_양식.xlsx')
}

// --- 화면 ------------------------------------------------------------------

function PreviewModal({ title, subtitle, fields, sheets, sheet, onSheet, parsed, onClose, onApply }) {
  const digits = fields.some((f) => f.key === 'currency') ? 2 : 0
  const show = (v) => (digits ? fmtFixed(v, digits) : fmt(v))
  const total = parsed?.rows?.reduce((s, l) => s + (Number(l.amount) || 0), 0) ?? 0
  const badRows = parsed?.rows?.filter((l) => !l.vendor || !l.amount) ?? []
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ width: 1040 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="row" style={{ padding: '10px 18px', flexWrap: 'wrap' }}>
          <span className="muted">{subtitle}</span>
          {sheets && sheets.length > 1 && (
            <>
              <span className="muted">시트</span>
              <select value={sheet} onChange={(e) => onSheet(e.target.value)}>
                {sheets.map((s) => <option key={s}>{s}</option>)}
              </select>
            </>
          )}
          {parsed && !parsed.error && (
            <span className="muted">
              {parsed.header ? `${parsed.header.row + 1}행을 머리글로 인식` : `머리글 없이 ${parsed.fixedOrder}개 열 순서로 인식`}
              {` · ${parsed.rows.length}건 · 합계 ${show(total)}`}
            </span>
          )}
        </div>

        {parsed?.error && <div className="error-box" style={{ margin: '0 18px' }}>{parsed.error}</div>}

        {parsed && !parsed.error && (
          <>
            {parsed.missing.length > 0 && (
              <div className="hint" style={{ margin: '0 18px' }}>인식하지 못한 열: {parsed.missing.join(', ')} — 해당 칸은 비워둔 채 가져옵니다.</div>
            )}
            {parsed.unmatchedCats.length > 0 && (
              <div className="hint" style={{ margin: '0 18px' }}>
                계정과목을 찾지 못했습니다: {parsed.unmatchedCats.join(', ')} — 가져온 뒤 직접 선택하세요.
              </div>
            )}
            {badRows.length > 0 && (
              <div className="hint" style={{ margin: '0 18px' }}>거래처 또는 금액이 빈 행 {badRows.length}건은 상신할 때 제외됩니다.</div>
            )}
            <div className="modal-table-wrap">
              <table className="modal-table">
                <thead>
                  <tr>
                    <th>행</th>
                    {fields.map((f) => <th key={f.key}>{f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.length === 0 && (
                    <tr><td colSpan={fields.length + 1} className="muted">가져올 내역이 없습니다</td></tr>
                  )}
                  {parsed.rows.map((l, i) => (
                    <tr key={i} className={!l.vendor || !l.amount ? 'hi' : ''}>
                      <td className="muted">{l._srcRow}</td>
                      {fields.map((f) => (
                        <td key={f.key}>
                          {f.key === 'amount' ? (l.amount === '' ? '' : show(l.amount)) : l[f.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="modal-footer">
          <span className="hint" style={{ flex: 1, margin: 0 }}>
            열 이름({fields.map((f) => f.label).join('·')})만 맞으면 순서는 달라도 됩니다.
          </span>
          <button className="btn secondary" onClick={onClose}>취소</button>
          <button className="btn secondary" onClick={() => onApply('append')} disabled={!parsed?.rows?.length}>기존 아래에 추가</button>
          <button className="btn" onClick={() => onApply('replace')} disabled={!parsed?.rows?.length}>기존 행 교체</button>
        </div>
      </div>
    </div>
  )
}

const stripMeta = (rows) => rows.map(({ _srcRow, ...l }) => l)

export default function LineExcelImport({ cats = [], fields = KRW_FIELDS, onApply }) {
  const fileRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [src, setSrc] = useState(null)      // { fileName, sheets, matrixOf }
  const [sheet, setSheet] = useState('')
  const fx = fields.some((f) => f.key === 'currency')

  const parsed = useMemo(() => {
    if (!src) return null
    try { return extract(src.matrixOf(sheet), cats, fields) } catch (e) { return { error: e.message } }
  }, [src, sheet, cats, fields])

  const pick = async (file) => {
    if (!file) return
    setBusy(true); setErr('')
    try {
      const s = await readFile(file)
      if (!s.sheets.length) throw new Error('시트가 없는 파일입니다')
      // 표지·안내 시트로 시작하는 파일이 흔해서, 내역이 실제로 읽히는 첫 시트를 고른다
      const usable = s.sheets.find((name) => {
        try { return extract(s.matrixOf(name), cats, fields).rows?.length } catch { return false }
      })
      setSrc({ fileName: file.name, ...s })
      setSheet(usable || s.sheets[0])
    } catch (e) {
      setErr(`엑셀을 읽지 못했습니다: ${e.message}`)
    } finally {
      setBusy(false)
    }
  }

  const close = () => { setSrc(null); setSheet('') }

  return (
    <>
      <div className="row">
        <button className="btn secondary" onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? '읽는 중…' : '엑셀 파일 선택'}
        </button>
        <button className="btn secondary" onClick={() => downloadTemplate(cats, fields, fx).catch((e) => setErr(e.message))}>
          양식 다운로드
        </button>
        <span className="hint" style={{ margin: 0 }}>.xlsx · .xlsm · .xls · .csv — 열 이름만 맞으면 순서와 시트는 자동으로 찾습니다</span>
      </div>
      <input
        ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" style={{ display: 'none' }}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }}
      />
      {err && <div className="hint warn">{err}</div>}

      {src && (
        <PreviewModal
          title={fx ? '엑셀에서 외화송금 내역 가져오기' : '엑셀에서 지출내역 가져오기'} subtitle={src.fileName}
          fields={fields} sheets={src.sheets} sheet={sheet} onSheet={setSheet}
          parsed={parsed} onClose={close}
          onApply={(mode) => { onApply(stripMeta(parsed.rows), mode); close() }}
        />
      )}
    </>
  )
}
